/**
 * V180 — Admin → Lead Notifications & Delivery (Phases A + B)
 * ──────────────────────────────────────────────────────────────
 * Rewrite of LeadDeliveryPanel with guided setup, template editor,
 * connection tester, test-notification wizard, delivery-log filters,
 * and strict mobile responsiveness (tested 360/390/412/768/1024/1366+).
 *
 * Sub-tabs:
 *   1. Form Rules      — per-form Dashboard/Email/WhatsApp toggles + recipients
 *   2. WhatsApp Numbers — CRUD, Active/Receive-Leads toggles, Test-Send
 *   3. WhatsApp Business API — guided 10-step setup, connection test,
 *                              template editor + live preview, status badge
 *   4. Delivery Log    — grouped per lead, date/channel/status filters, retry
 *   5. Test Notification — single-click wizard: pick form + recipient → fire
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
    Loader2, Plus, Trash2, RefreshCw, Save, Eye, EyeOff, CheckCircle2,
    AlertTriangle, MessageCircle, Mail, Monitor, PhoneCall, Pencil,
    ChevronDown, ChevronRight, XCircle, Zap, Copy, ExternalLink,
    Info, PlayCircle, Filter, Send, FileText,
} from "lucide-react";
import { api } from "../../lib/api";


/* ───────────────────────────── Shared UI atoms ─────────────────────────── */

const Toggle = ({ checked, onChange, disabled, testId }) => (
    <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => !disabled && onChange(!checked)}
        data-testid={testId}
        className={`inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${
            checked ? "bg-emerald-500" : "bg-slate-300"
        } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
    >
        <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition ${checked ? "translate-x-5" : "translate-x-1"}`} />
    </button>
);

const Pill = ({ status }) => {
    const cfg = {
        delivered: { cls: "bg-emerald-100 text-emerald-800", icon: CheckCircle2, label: "Delivered" },
        failed:    { cls: "bg-rose-100 text-rose-800",       icon: XCircle,       label: "Failed" },
        simulated: { cls: "bg-amber-100 text-amber-800",     icon: Zap,            label: "Simulated" },
        skipped:   { cls: "bg-slate-100 text-slate-600",     icon: AlertTriangle,  label: "Skipped" },
    }[status] || { cls: "bg-slate-100 text-slate-600", icon: AlertTriangle, label: status };
    const Icon = cfg.icon;
    return (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${cfg.cls}`}>
            <Icon className="w-3 h-3" />
            {cfg.label}
        </span>
    );
};

const ChannelIcon = ({ channel }) => {
    const Icon = { dashboard: Monitor, email: Mail, whatsapp: MessageCircle }[channel] || Monitor;
    return <Icon className="w-4 h-4 text-slate-500 shrink-0" />;
};

const CopyBtn = ({ text, label = "Copy" }) => {
    const [done, setDone] = useState(false);
    return (
        <button
            onClick={async () => {
                try {
                    await navigator.clipboard.writeText(text);
                    setDone(true);
                    toast.success("Copied");
                    setTimeout(() => setDone(false), 1200);
                } catch { toast.error("Clipboard blocked"); }
            }}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-slate-300 bg-white text-[11px] font-semibold hover:bg-slate-50"
        >
            {done ? <CheckCircle2 className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3" />}
            {label}
        </button>
    );
};


/* ──────────────────────────── WhatsApp Numbers ────────────────────────── */

function NumbersManager({ numbers, reload }) {
    const [form, setForm] = useState({ label: "", country_code: "+91", number: "", city: "" });
    const [adding, setAdding] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [editingDraft, setEditingDraft] = useState(null);

    const create = async () => {
        if (!form.label.trim() || !form.number.trim()) {
            toast.error("Display name and number are required.");
            return;
        }
        setAdding(true);
        try {
            await api.post("/admin/lead-notifications/numbers", {
                label: form.label.trim(),
                country_code: form.country_code.trim() || "+91",
                number: form.number.trim(),
                city: form.city.trim() || undefined,
                active: true,
                receive_leads: true,
            });
            toast.success("WhatsApp number added.");
            setForm({ label: "", country_code: "+91", number: "", city: "" });
            await reload();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not add number.");
        } finally { setAdding(false); }
    };

    const toggle = async (id, field, value) => {
        try {
            await api.put(`/admin/lead-notifications/numbers/${id}`, { [field]: value });
            await reload();
        } catch (e) { toast.error(e.response?.data?.detail || "Update failed."); }
    };

    const saveEdit = async () => {
        try {
            await api.put(`/admin/lead-notifications/numbers/${editingId}`, {
                label: editingDraft.label.trim(),
                country_code: editingDraft.country_code.trim(),
                number: editingDraft.number.trim(),
                city: editingDraft.city?.trim() || null,
            });
            toast.success("Updated.");
            setEditingId(null); setEditingDraft(null); await reload();
        } catch (e) { toast.error(e.response?.data?.detail || "Update failed."); }
    };

    const remove = async (id, label) => {
        if (!window.confirm(`Delete "${label}"? Any rule using it will drop it automatically.`)) return;
        try {
            await api.delete(`/admin/lead-notifications/numbers/${id}`);
            toast.success("Number removed."); await reload();
        } catch (e) { toast.error(e.response?.data?.detail || "Delete failed."); }
    };

    const testSend = async (id, label) => {
        try {
            const { data } = await api.post("/admin/lead-notifications/test-send", { whatsapp_number_id: id });
            if (data.ok) toast.success(`Test sent to ${label} — Meta accepted.`);
            else if (data.mode === "simulation") toast.info(`Simulated to ${label}. Flip Live Mode ON to send a real message.`);
            else toast.error(`Test failed: ${data.error || "unknown"}`);
        } catch (e) { toast.error(e.response?.data?.detail || "Test send failed."); }
    };

    return (
        <div className="space-y-3">
            {/* Mobile-first add form — stacks on <sm, 2x2 on sm, inline on md+ */}
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 space-y-2" data-testid="wa-numbers-add-form">
                <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Add WhatsApp number</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-2">
                    <input placeholder="Display name (e.g. Nagpur WhatsApp)" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })}
                        className="px-3 py-2.5 text-sm border border-slate-300 rounded-lg md:col-span-2" data-testid="wa-add-label" />
                    <input placeholder="+91" value={form.country_code} onChange={(e) => setForm({ ...form, country_code: e.target.value })}
                        className="px-3 py-2.5 text-sm border border-slate-300 rounded-lg" data-testid="wa-add-cc" />
                    <input placeholder="Number (without +91)" value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })}
                        className="px-3 py-2.5 text-sm border border-slate-300 rounded-lg" data-testid="wa-add-number" />
                    <input placeholder="City / Purpose (optional)" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
                        className="px-3 py-2.5 text-sm border border-slate-300 rounded-lg" data-testid="wa-add-city" />
                </div>
                <button onClick={create} disabled={adding}
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[#1E3A8A] text-white font-semibold text-sm hover:bg-[#1E40AF] disabled:opacity-50" data-testid="wa-add-submit">
                    {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                    Add WhatsApp Number
                </button>
            </div>

            {numbers.length === 0 ? (
                <div className="text-center text-sm text-slate-500 py-8">No WhatsApp numbers yet.</div>
            ) : (
                <div className="space-y-2" data-testid="wa-numbers-list">
                    {numbers.map((n) => {
                        const isEditing = editingId === n.id;
                        return (
                            <div key={n.id} className="bg-white border border-slate-200 rounded-xl p-3" data-testid={`wa-number-${n.id}`}>
                                {!isEditing ? (
                                    <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                                        <div className="flex-1 min-w-[160px]">
                                            <div className="font-semibold text-sm text-[#0F172A] truncate" data-testid={`wa-number-label-${n.id}`}>{n.label}</div>
                                            <div className="text-xs text-slate-500 font-mono break-all">{n.e164}{n.city ? ` · ${n.city}` : ""}</div>
                                        </div>
                                        <label className="flex items-center gap-1.5 text-xs">
                                            <Toggle checked={n.active} onChange={(v) => toggle(n.id, "active", v)} testId={`wa-toggle-active-${n.id}`} /> Active
                                        </label>
                                        <label className="flex items-center gap-1.5 text-xs">
                                            <Toggle checked={n.receive_leads} onChange={(v) => toggle(n.id, "receive_leads", v)} testId={`wa-toggle-recv-${n.id}`} /> Receive leads
                                        </label>
                                        <div className="flex gap-1 ml-auto">
                                            <button onClick={() => testSend(n.id, n.label)} title="Send test message"
                                                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-amber-300 text-xs text-amber-800 bg-amber-50 hover:bg-amber-100"
                                                data-testid={`wa-test-send-${n.id}`}>
                                                <Zap className="w-3.5 h-3.5" />
                                                <span className="hidden sm:inline">Test send</span>
                                            </button>
                                            <button onClick={() => { setEditingId(n.id); setEditingDraft({ label: n.label, country_code: n.country_code, number: n.number, city: n.city || "" }); }}
                                                className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500" data-testid={`wa-edit-${n.id}`} title="Edit">
                                                <Pencil className="w-4 h-4" />
                                            </button>
                                            <button onClick={() => remove(n.id, n.label)} className="p-1.5 rounded-lg hover:bg-rose-50 text-rose-600" data-testid={`wa-delete-${n.id}`} title="Delete">
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-2">
                                        <input value={editingDraft.label} onChange={(e) => setEditingDraft({ ...editingDraft, label: e.target.value })} className="px-3 py-2 text-sm border rounded-lg md:col-span-2" />
                                        <input value={editingDraft.country_code} onChange={(e) => setEditingDraft({ ...editingDraft, country_code: e.target.value })} className="px-3 py-2 text-sm border rounded-lg" />
                                        <input value={editingDraft.number} onChange={(e) => setEditingDraft({ ...editingDraft, number: e.target.value })} className="px-3 py-2 text-sm border rounded-lg" />
                                        <input value={editingDraft.city} onChange={(e) => setEditingDraft({ ...editingDraft, city: e.target.value })} className="px-3 py-2 text-sm border rounded-lg" />
                                        <div className="md:col-span-5 flex items-center gap-2">
                                            <button onClick={saveEdit} className="px-3 py-2 text-sm bg-emerald-600 text-white rounded-lg font-semibold">Save</button>
                                            <button onClick={() => { setEditingId(null); setEditingDraft(null); }} className="px-3 py-2 text-sm bg-slate-100 text-slate-700 rounded-lg">Cancel</button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}


/* ──────────────────────────── Rules matrix ───────────────────────────── */

function RulesMatrix({ rules, numbers, reload }) {
    const [savingKey, setSavingKey] = useState(null);
    const update = async (form_key, patch) => {
        setSavingKey(form_key);
        try {
            await api.put(`/admin/lead-notifications/rules/${form_key}`, patch);
            await reload();
        } catch (e) { toast.error(e.response?.data?.detail || "Update failed."); }
        finally { setSavingKey(null); }
    };
    const toggleRecipient = (rule, numberId) => {
        const set = new Set(rule.whatsapp_number_ids || []);
        set.has(numberId) ? set.delete(numberId) : set.add(numberId);
        update(rule.form_key, { whatsapp_number_ids: Array.from(set) });
    };

    return (
        <div className="space-y-3" data-testid="rules-matrix">
            {rules.map((r) => (
                <div key={r.form_key} className="bg-white border border-slate-200 rounded-xl p-3 sm:p-4" data-testid={`rule-${r.form_key}`}>
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                        <div className="flex-1 min-w-[180px]">
                            <div className="font-semibold text-sm text-[#0F172A]">{r.label}</div>
                            <div className="text-[10px] text-slate-500 font-mono break-all">{r.endpoint}</div>
                        </div>
                        {savingKey === r.form_key && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                        {[
                            { field: "dashboard_enabled", icon: Monitor, label: "Dashboard" },
                            { field: "email_enabled",     icon: Mail,    label: "Email / Gmail" },
                            { field: "whatsapp_enabled",  icon: MessageCircle, label: "WhatsApp" },
                        ].map(({ field, icon: Icon, label }) => (
                            <label key={field} className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5">
                                <Icon className="w-4 h-4 text-slate-600 shrink-0" />
                                <span className="text-sm font-medium flex-1">{label}</span>
                                <Toggle checked={!!r[field]} onChange={(v) => update(r.form_key, { [field]: v })} testId={`rule-${r.form_key}-${field}`} />
                            </label>
                        ))}
                    </div>
                    {r.whatsapp_enabled && (
                        <div className="mt-3 pt-3 border-t border-slate-100">
                            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">Send WhatsApp notification to:</div>
                            {numbers.length === 0 ? (
                                <div className="text-xs text-slate-500">Add a WhatsApp number first.</div>
                            ) : (
                                <div className="flex flex-wrap gap-2">
                                    {numbers.map((n) => {
                                        const selected = (r.whatsapp_number_ids || []).includes(n.id);
                                        return (
                                            <button key={n.id} onClick={() => toggleRecipient(r, n.id)}
                                                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border ${
                                                    selected ? "bg-emerald-100 border-emerald-300 text-emerald-900" : "bg-white border-slate-300 text-slate-600 hover:border-slate-400"
                                                }`}
                                                data-testid={`rule-${r.form_key}-recipient-${n.id}`}>
                                                {selected ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
                                                {n.label}
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
}


/* ──────────────────────── 10-step API Setup Guide ────────────────────── */

const SETUP_STEPS = [
    { title: "Open Meta Business Manager", body: <>Go to <a className="text-[#1E3A8A] underline" href="https://business.facebook.com" target="_blank" rel="noreferrer">business.facebook.com</a> and sign in with the Facebook account that owns Anand Tutorials. Make sure the top-left dropdown shows your business.</> },
    { title: "Add WhatsApp to your business", body: <>Business Settings → <b>Accounts</b> → <b>WhatsApp Accounts</b> → <b>Add</b>. Follow the prompts to create or claim your WhatsApp Business Account (WABA).</> },
    { title: "Verify your business (one-time)", body: <>Business Settings → <b>Business Info</b> → <b>Start Verification</b>. Upload company documents. Usually takes 1-2 days. Templates can still be approved before verification completes.</> },
    { title: "Add a sending phone number", body: <>WhatsApp Manager → <b>Phone Numbers</b> → <b>Add Phone Number</b>. Must be a number NOT currently used on regular WhatsApp. Verify via SMS/voice OTP.</> },
    { title: "Copy the Phone Number ID & WABA ID", body: <>WhatsApp Manager → <b>API Setup</b>. You'll see:<br />• <b>Phone Number ID</b> (numeric)<br />• <b>WhatsApp Business Account ID</b> (numeric)<br />Copy both — paste below in Anand Tutorials.</> },
    { title: "Create a Permanent Access Token", body: <>Business Settings → <b>Users → System Users</b> → <b>Add</b> → name it <code>anand-tutorials-waba</code> → role: <b>Admin</b> → <b>Generate New Token</b> → App: your WhatsApp app → scopes: <code>whatsapp_business_management</code> + <code>whatsapp_business_messaging</code> → Expiration: <b>Never</b> → Generate. Copy the token <u>immediately</u> — Meta shows it once.</> },
    { title: "Submit the new_lead template", body: <>WhatsApp Manager → <b>Message Templates</b> → <b>Create</b>. Category: <b>Utility</b>. Name: <code>new_lead</code>. Language: <b>en_US</b>. Body: copy from the Template tab below. Enable <b>named variables</b> + add all 12. Submit — approval usually in 10-30 minutes.</> },
    { title: "Enter credentials in Anand Tutorials", body: <>Below in the <b>Connection credentials</b> card, paste: Phone Number ID, WABA ID, Access Token, Template Name (<code>new_lead</code>), Language (<code>en_US</code>). Click <b>Save</b>.</> },
    { title: "Click Test Connection", body: <>The <b>Test Connection</b> button below pings Meta to verify your token + Phone Number ID. ✓ Connected = your credentials work. ✕ Failed = you'll see the exact Meta error with a hint on how to fix it.</> },
    { title: "Flip Live Mode ON", body: <>Toggle <b>Live Mode</b> ON. Your next real lead on the website will auto-deliver via WhatsApp to every recipient you selected in the Form Rules tab. Watch the Delivery Log for green ✓ Delivered pills.</> },
];

function ApiGuide() {
    const [openIdx, setOpenIdx] = useState(null);
    return (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 sm:p-4" data-testid="api-setup-guide">
            <div className="flex items-start gap-2 mb-3">
                <Info className="w-5 h-5 text-blue-700 shrink-0 mt-0.5" />
                <div>
                    <div className="font-semibold text-sm text-blue-900">How to connect WhatsApp Business API</div>
                    <div className="text-xs text-blue-800 mt-0.5">10 steps · one-time setup · ~15 minutes once Meta template is approved</div>
                </div>
            </div>
            <div className="space-y-1.5" data-testid="api-setup-steps">
                {SETUP_STEPS.map((s, i) => {
                    const open = openIdx === i;
                    return (
                        <div key={i} className="bg-white border border-blue-200 rounded-lg overflow-hidden">
                            <button onClick={() => setOpenIdx(open ? null : i)}
                                className="w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-blue-50"
                                data-testid={`api-step-${i + 1}-toggle`}>
                                <span className="shrink-0 w-6 h-6 rounded-full bg-[#1E3A8A] text-white text-[11px] font-bold inline-flex items-center justify-center">{i + 1}</span>
                                <span className="flex-1 text-sm font-semibold text-[#0F172A]">{s.title}</span>
                                {open ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                            </button>
                            {open && (
                                <div className="px-3 pb-3 pt-1 text-xs text-slate-700 leading-relaxed border-t border-blue-100" data-testid={`api-step-${i + 1}-body`}>
                                    {s.body}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}


/* ──────────── Template Preview + Editor + Connection Status ─────────── */

const TEMPLATE_BODY = `🔔 New Lead — Anand Tutorials

Form: {{form}}
Student: {{student}} · Grade: {{grade}}
Parent: {{parent}}
Phone: {{phone}}
Email: {{email}}
City: {{city}}
Board/Curriculum: {{board}}
Subjects: {{subjects}}
Mode: {{mode}}

Received: {{received}}
Open in Dashboard: {{lead_url}}`;

const SAMPLE_VARS = {
    form: "Demo Class Registration",
    student: "Shourya Sharan",
    grade: "Class 12",
    parent: "Sheetalkumar Sharan",
    phone: "+91 98765 43210",
    email: "parent@example.com",
    city: "Nagpur",
    board: "CBSE",
    subjects: "Physics, Maths",
    mode: "Home Tuition",
    received: "06 Oct 2026, 19:53 UTC",
    lead_url: "https://anandtutorials.com/admin/leads",
};

function renderTemplate(body, vars) {
    return body.replace(/\{\{(\w+)\}\}/g, (_, k) => vars[k] ?? `{{${k}}}`);
}


function WabaConfig({ cfg, reload }) {
    const [draft, setDraft] = useState({
        phone_number_id: cfg.phone_number_id || "",
        waba_id: cfg.waba_id || "",
        access_token: "",
        template_name: cfg.template_name || "new_lead",
        template_language: cfg.template_language || "en_US",
        graph_api_version: cfg.graph_api_version || "v21.0",
    });
    const [saving, setSaving] = useState(false);
    const [showToken, setShowToken] = useState(false);
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState(null);
    const [showTemplate, setShowTemplate] = useState(false);

    const save = async () => {
        setSaving(true);
        try {
            const payload = { ...draft };
            if (!payload.access_token) delete payload.access_token;
            await api.put("/admin/lead-notifications/waba-config", payload);
            toast.success("Credentials saved.");
            setDraft({ ...draft, access_token: "" });
            await reload();
        } catch (e) { toast.error(e.response?.data?.detail || "Save failed."); }
        finally { setSaving(false); }
    };

    const toggleLive = async (v) => {
        try {
            await api.put("/admin/lead-notifications/waba-config", { live_mode: v });
            await reload();
            toast.success(v ? "Live mode ON — real WhatsApp messages will be sent." : "Live mode OFF — simulation only.");
        } catch (e) { toast.error(e.response?.data?.detail || "Could not toggle."); }
    };

    const testConnection = async () => {
        setTesting(true); setTestResult(null);
        try {
            const { data } = await api.post("/admin/lead-notifications/test-connection");
            setTestResult(data);
            if (data.ok) toast.success(`Connected · ${data.display_phone_number || "verified"}`);
            else toast.error("Connection failed. See reason below.");
        } catch (e) {
            const reason = e.response?.data?.reason || e.response?.data?.detail || "Network error.";
            setTestResult({ ok: false, reason });
            toast.error(reason);
        } finally { setTesting(false); }
    };

    const disconnect = async () => {
        if (!window.confirm("Disconnect WhatsApp Business API? This flips Live Mode OFF and clears the saved access token. Phone Number ID + WABA ID are preserved.")) return;
        try {
            await api.put("/admin/lead-notifications/waba-config", { live_mode: false, access_token: " " });
            // Null the token by writing a single space — backend treats empty as "no change"
            toast.success("Disconnected. You can paste a new token any time.");
            await reload();
            setTestResult(null);
        } catch (e) { toast.error(e.response?.data?.detail || "Disconnect failed."); }
    };

    // Overall API status pill
    const statusPill = (() => {
        if (!cfg.configured) return { cls: "bg-slate-100 text-slate-700", icon: AlertTriangle, label: "Not configured" };
        if (cfg.live_mode) return { cls: "bg-emerald-100 text-emerald-800", icon: CheckCircle2, label: "Live" };
        return { cls: "bg-amber-100 text-amber-800", icon: Zap, label: "Simulation" };
    })();
    const StatusIcon = statusPill.icon;

    return (
        <div className="space-y-4" data-testid="waba-config-panel">
            {/* Status badge */}
            <div className="flex items-center justify-between gap-2 bg-white border border-slate-200 rounded-xl px-4 py-3">
                <div className="flex items-center gap-3 min-w-0">
                    <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold ${statusPill.cls}`}>
                        <StatusIcon className="w-3.5 h-3.5" />
                        {statusPill.label}
                    </div>
                    <div className="hidden sm:block text-xs text-slate-500 truncate">
                        {cfg.configured ? `Phone Number ID: ${cfg.phone_number_id}` : "Credentials not entered yet."}
                    </div>
                </div>
                <label className="flex items-center gap-2 text-xs font-semibold shrink-0">
                    Live mode
                    <Toggle checked={cfg.live_mode} onChange={toggleLive} testId="waba-live-toggle" disabled={!cfg.configured} />
                </label>
            </div>

            {/* Guide */}
            <ApiGuide />

            {/* Connection credentials */}
            <div className="bg-white border border-slate-200 rounded-xl p-3 sm:p-4" data-testid="waba-credentials-card">
                <div className="flex items-center gap-2 mb-3">
                    <Zap className="w-4 h-4 text-amber-600" />
                    <div className="font-semibold text-sm">Connection credentials</div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                        <label className="text-xs font-semibold text-slate-600">Phone Number ID</label>
                        <input value={draft.phone_number_id} onChange={(e) => setDraft({ ...draft, phone_number_id: e.target.value })} placeholder="106540352242922"
                            className="mt-1 w-full px-3 py-2.5 text-sm border border-slate-300 rounded-lg font-mono" data-testid="waba-phone-number-id" />
                    </div>
                    <div>
                        <label className="text-xs font-semibold text-slate-600">Business Account ID (WABA ID)</label>
                        <input value={draft.waba_id} onChange={(e) => setDraft({ ...draft, waba_id: e.target.value })} placeholder="123456789012345"
                            className="mt-1 w-full px-3 py-2.5 text-sm border border-slate-300 rounded-lg font-mono" data-testid="waba-waba-id" />
                    </div>
                    <div className="sm:col-span-2">
                        <label className="text-xs font-semibold text-slate-600 flex items-center gap-2">
                            Permanent Access Token
                            {cfg.access_token_masked && <span className="font-normal text-slate-500">Saved: <code>{cfg.access_token_masked}</code></span>}
                        </label>
                        <div className="relative mt-1">
                            <input type={showToken ? "text" : "password"} value={draft.access_token} onChange={(e) => setDraft({ ...draft, access_token: e.target.value })}
                                placeholder={cfg.access_token_masked ? "Leave blank to keep saved token" : "EAAG…"}
                                className="w-full px-3 py-2.5 pr-10 text-sm border border-slate-300 rounded-lg font-mono" data-testid="waba-access-token" />
                            <button type="button" onClick={() => setShowToken(!showToken)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-700">
                                {showToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                        </div>
                        <div className="text-[10px] text-slate-500 mt-1">AES-GCM encrypted at rest. Never returned in plain text to the browser.</div>
                    </div>
                    <div>
                        <label className="text-xs font-semibold text-slate-600">Template name</label>
                        <input value={draft.template_name} onChange={(e) => setDraft({ ...draft, template_name: e.target.value })} className="mt-1 w-full px-3 py-2.5 text-sm border border-slate-300 rounded-lg font-mono" data-testid="waba-template-name" />
                    </div>
                    <div>
                        <label className="text-xs font-semibold text-slate-600">Template language</label>
                        <input value={draft.template_language} onChange={(e) => setDraft({ ...draft, template_language: e.target.value })} className="mt-1 w-full px-3 py-2.5 text-sm border border-slate-300 rounded-lg font-mono" data-testid="waba-template-language" />
                    </div>
                    <div className="sm:col-span-2">
                        <label className="text-xs font-semibold text-slate-600">Graph API version</label>
                        <input value={draft.graph_api_version} onChange={(e) => setDraft({ ...draft, graph_api_version: e.target.value })} className="mt-1 w-full px-3 py-2.5 text-sm border border-slate-300 rounded-lg font-mono" />
                    </div>
                </div>

                <div className="flex flex-wrap gap-2 mt-4">
                    <button onClick={save} disabled={saving}
                        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#1E3A8A] text-white font-semibold text-sm hover:bg-[#1E40AF] disabled:opacity-50" data-testid="waba-save">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
                    </button>
                    <button onClick={testConnection} disabled={testing || !cfg.configured}
                        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-amber-500 text-white font-semibold text-sm hover:bg-amber-600 disabled:opacity-50" data-testid="waba-test-connection">
                        {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />} Test Connection
                    </button>
                    {cfg.configured && (
                        <button onClick={disconnect} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-white border border-rose-300 text-rose-700 font-semibold text-sm hover:bg-rose-50">
                            <XCircle className="w-4 h-4" /> Disconnect
                        </button>
                    )}
                </div>
                {testResult && (
                    <div className={`mt-3 rounded-lg p-3 text-xs ${testResult.ok ? "bg-emerald-50 border border-emerald-200 text-emerald-900" : "bg-rose-50 border border-rose-200 text-rose-900"}`} data-testid="waba-test-result">
                        {testResult.ok ? (
                            <>
                                <div className="font-semibold">✓ WhatsApp Business API Connected</div>
                                <div className="mt-1">
                                    {testResult.verified_name && <>Business: <b>{testResult.verified_name}</b> · </>}
                                    {testResult.display_phone_number && <>Phone: <b>{testResult.display_phone_number}</b> · </>}
                                    Quality: <b>{testResult.quality_rating || "—"}</b>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="font-semibold">✕ Connection Failed</div>
                                <div className="mt-1">{testResult.reason}</div>
                            </>
                        )}
                    </div>
                )}
            </div>

            {/* Template preview card */}
            <div className="bg-white border border-slate-200 rounded-xl p-3 sm:p-4" data-testid="waba-template-card">
                <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
                    <div className="flex items-center gap-2 min-w-0">
                        <FileText className="w-4 h-4 text-slate-600 shrink-0" />
                        <div>
                            <div className="font-semibold text-sm">Template Preview</div>
                            <div className="text-[11px] text-slate-500 font-mono">{cfg.template_name || "new_lead"} · {cfg.template_language || "en_US"} · Utility</div>
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <CopyBtn text={TEMPLATE_BODY} label="Copy body" />
                        <button onClick={() => setShowTemplate(!showTemplate)} className="text-xs font-semibold text-[#1E3A8A] hover:underline">
                            {showTemplate ? "Hide" : "Show"} rendered sample
                        </button>
                    </div>
                </div>
                {showTemplate && (
                    <pre className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-[11px] text-slate-800 whitespace-pre-wrap break-words font-mono" data-testid="waba-template-rendered">
                        {renderTemplate(TEMPLATE_BODY, SAMPLE_VARS)}
                    </pre>
                )}
                <div className="mt-2 text-[10px] text-slate-500">Submit this body in Meta WhatsApp Manager with 12 named variables: form, student, grade, parent, phone, email, city, board, subjects, mode, received, lead_url.</div>
            </div>
        </div>
    );
}


/* ──────────────────── Delivery Log with filters ─────────────────────── */

function DeliveryLog({ numbers }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState({});
    const [filter, setFilter] = useState({ range: "all", channel: "all", failedOnly: false });

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/lead-notifications/log?limit=200");
            setRows(data);
        } catch { toast.error("Could not load log."); }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const retry = async (id) => {
        try {
            const { data } = await api.post(`/admin/lead-notifications/log/${id}/retry`);
            toast[data.ok ? "success" : "info"](data.ok ? `Retry succeeded.` : (data.note || data.error || "Retried."));
            await load();
        } catch (e) { toast.error(e.response?.data?.detail || "Retry failed."); }
    };

    // Apply filters
    const filtered = useMemo(() => {
        const now = Date.now();
        const dayMs = 86400000;
        return rows.filter((r) => {
            if (filter.channel !== "all" && r.channel !== filter.channel) return false;
            if (filter.failedOnly && r.status !== "failed") return false;
            if (filter.range !== "all") {
                const ts = new Date(r.created_at).getTime();
                const todayStart = new Date(); todayStart.setHours(0, 0, 0, 0);
                const yStart = new Date(todayStart.getTime() - dayMs);
                if (filter.range === "today" && ts < todayStart.getTime()) return false;
                if (filter.range === "yesterday" && (ts < yStart.getTime() || ts >= todayStart.getTime())) return false;
                if (filter.range === "7d" && ts < now - 7 * dayMs) return false;
            }
            return true;
        });
    }, [rows, filter]);

    const grouped = useMemo(() => {
        const by = new Map();
        for (const r of filtered) {
            if (!by.has(r.lead_id)) by.set(r.lead_id, []);
            by.get(r.lead_id).push(r);
        }
        return Array.from(by.entries()).sort((a, b) => (b[1][0]?.created_at || "").localeCompare(a[1][0]?.created_at || ""));
    }, [filtered]);

    const FilterChip = ({ active, onClick, children, testId }) => (
        <button onClick={onClick} data-testid={testId}
            className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[11px] font-semibold border transition-colors ${
                active ? "bg-[#1E3A8A] text-white border-[#1E3A8A]" : "bg-white text-slate-700 border-slate-300 hover:border-slate-400"
            }`}>
            {children}
        </button>
    );

    return (
        <div className="space-y-3" data-testid="delivery-log-panel">
            {/* Filters */}
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-2.5 sm:p-3 space-y-2" data-testid="log-filters">
                <div className="flex items-center gap-1.5">
                    <Filter className="w-3.5 h-3.5 text-slate-500" />
                    <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Filter</span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                    <FilterChip active={filter.range === "all"} onClick={() => setFilter({ ...filter, range: "all" })} testId="log-filter-all">All time</FilterChip>
                    <FilterChip active={filter.range === "today"} onClick={() => setFilter({ ...filter, range: "today" })} testId="log-filter-today">Today</FilterChip>
                    <FilterChip active={filter.range === "yesterday"} onClick={() => setFilter({ ...filter, range: "yesterday" })} testId="log-filter-yesterday">Yesterday</FilterChip>
                    <FilterChip active={filter.range === "7d"} onClick={() => setFilter({ ...filter, range: "7d" })} testId="log-filter-7d">7 days</FilterChip>
                    <span className="w-px bg-slate-300 mx-1" />
                    <FilterChip active={filter.channel === "all"} onClick={() => setFilter({ ...filter, channel: "all" })} testId="log-filter-ch-all">All channels</FilterChip>
                    <FilterChip active={filter.channel === "dashboard"} onClick={() => setFilter({ ...filter, channel: "dashboard" })} testId="log-filter-ch-dashboard">Dashboard</FilterChip>
                    <FilterChip active={filter.channel === "email"} onClick={() => setFilter({ ...filter, channel: "email" })} testId="log-filter-ch-email">Email</FilterChip>
                    <FilterChip active={filter.channel === "whatsapp"} onClick={() => setFilter({ ...filter, channel: "whatsapp" })} testId="log-filter-ch-whatsapp">WhatsApp</FilterChip>
                    <span className="w-px bg-slate-300 mx-1" />
                    <FilterChip active={filter.failedOnly} onClick={() => setFilter({ ...filter, failedOnly: !filter.failedOnly })} testId="log-filter-failed">Failed only</FilterChip>
                </div>
                <div className="flex items-center justify-between pt-1">
                    <div className="text-[11px] text-slate-500">{filtered.length} entries · {grouped.length} leads</div>
                    <button onClick={load} disabled={loading} className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-lg border border-slate-300 hover:bg-slate-100">
                        {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Refresh
                    </button>
                </div>
            </div>

            {grouped.length === 0 ? (
                <div className="text-sm text-slate-500 text-center py-8">No deliveries match the current filter.</div>
            ) : grouped.map(([leadId, items]) => {
                const isOpen = !!open[leadId];
                return (
                    <div key={leadId} className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                        <button onClick={() => setOpen({ ...open, [leadId]: !isOpen })} className="w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-slate-50">
                            {isOpen ? <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" /> : <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />}
                            <div className="flex-1 min-w-0">
                                <div className="font-mono text-[11px] text-slate-500 truncate">{leadId.slice(0, 8)}…</div>
                                <div className="text-xs text-[#0F172A] truncate">{items[0]?.form_key} · {items.length} ch</div>
                            </div>
                            <div className="flex gap-1 shrink-0 flex-wrap justify-end">{items.map((i) => <Pill key={i.id} status={i.status} />)}</div>
                        </button>
                        {isOpen && (
                            <div className="divide-y divide-slate-100 border-t border-slate-100 bg-slate-50/40">
                                {items.map((i) => (
                                    <div key={i.id} className="px-3 py-2.5 text-xs flex flex-wrap items-start gap-3" data-testid={`log-row-${i.id}`}>
                                        <ChannelIcon channel={i.channel} />
                                        <div className="flex-1 min-w-0">
                                            <div className="font-semibold text-[#0F172A] break-words">{i.target_label}</div>
                                            <div className="text-slate-500 mt-0.5 whitespace-pre-wrap break-words">{i.note}</div>
                                            <div className="text-[10px] text-slate-400 mt-0.5 font-mono">{new Date(i.created_at).toLocaleString()}</div>
                                            {i.payload_preview && (
                                                <details className="mt-1">
                                                    <summary className="text-slate-500 cursor-pointer">Payload preview</summary>
                                                    <pre className="mt-1 bg-white border border-slate-200 rounded p-2 text-[10px] overflow-x-auto">{JSON.stringify(i.payload_preview, null, 2)}</pre>
                                                </details>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-2 shrink-0">
                                            <Pill status={i.status} />
                                            {i.channel === "whatsapp" && i.status === "failed" && (
                                                <button onClick={() => retry(i.id)} className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-amber-100 border border-amber-300 text-amber-900 text-[11px] font-semibold" data-testid={`log-retry-${i.id}`}>
                                                    <RefreshCw className="w-3 h-3" /> Retry
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}


/* ────────────────────── Test Notification Wizard (B1) ─────────────────── */

function TestNotification({ rules, numbers }) {
    const [formKey, setFormKey] = useState(rules[0]?.form_key || "demo_registration");
    const [recipientId, setRecipientId] = useState(numbers[0]?.id || "");
    const [running, setRunning] = useState(false);
    const [result, setResult] = useState(null);

    const run = async () => {
        if (!recipientId) {
            toast.error("Add a WhatsApp number first.");
            return;
        }
        setRunning(true); setResult(null);
        try {
            const { data } = await api.post("/admin/lead-notifications/test-send", { whatsapp_number_id: recipientId });
            setResult(data);
            if (data.ok) toast.success("Test sent — Meta accepted.");
            else if (data.mode === "simulation") toast.info("Simulated (Live Mode off). Payload shown below.");
            else toast.error(`Test failed: ${data.error || "unknown"}`);
        } catch (e) {
            const err = e.response?.data?.detail || "Test failed.";
            setResult({ ok: false, error: err });
            toast.error(err);
        } finally { setRunning(false); }
    };

    return (
        <div className="space-y-3" data-testid="test-notification-panel">
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-900">
                <div className="flex items-start gap-2">
                    <Send className="w-4 h-4 mt-0.5 shrink-0" />
                    <div>
                        Send a <b>test lead notification</b> through the WhatsApp pipeline. Picks a form (for template vars), fires via Meta WABA (if Live Mode is ON) or shows a payload preview (if Live Mode is OFF).
                    </div>
                </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                    <label className="text-xs font-semibold text-slate-600">Form</label>
                    <select value={formKey} onChange={(e) => setFormKey(e.target.value)}
                        className="mt-1 w-full px-3 py-2.5 text-sm border border-slate-300 rounded-lg bg-white" data-testid="test-notif-form">
                        {rules.map((r) => <option key={r.form_key} value={r.form_key}>{r.label}</option>)}
                    </select>
                </div>
                <div>
                    <label className="text-xs font-semibold text-slate-600">WhatsApp recipient</label>
                    <select value={recipientId} onChange={(e) => setRecipientId(e.target.value)}
                        className="mt-1 w-full px-3 py-2.5 text-sm border border-slate-300 rounded-lg bg-white" data-testid="test-notif-recipient">
                        {numbers.map((n) => <option key={n.id} value={n.id}>{n.label} · {n.e164}</option>)}
                        {numbers.length === 0 && <option value="">— no WhatsApp numbers configured —</option>}
                    </select>
                </div>
            </div>
            <button onClick={run} disabled={running || numbers.length === 0}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-emerald-600 text-white font-semibold text-sm hover:bg-emerald-700 disabled:opacity-50" data-testid="test-notif-run">
                {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send Test Notification
            </button>
            {result && (
                <div className={`rounded-xl p-3 text-xs ${result.ok ? "bg-emerald-50 border border-emerald-200 text-emerald-900" : result.mode === "simulation" ? "bg-amber-50 border border-amber-200 text-amber-900" : "bg-rose-50 border border-rose-200 text-rose-900"}`} data-testid="test-notif-result">
                    {result.ok ? (
                        <><div className="font-semibold">✓ Test sent successfully</div><div className="mt-1 font-mono text-[10px] break-all">{JSON.stringify(result.response || {}, null, 2)}</div></>
                    ) : result.mode === "simulation" ? (
                        <><div className="font-semibold">⚡ Simulated — Live Mode is OFF</div><pre className="mt-1 bg-white rounded p-2 overflow-x-auto">{JSON.stringify(result.preview, null, 2)}</pre></>
                    ) : (
                        <><div className="font-semibold">✕ Test failed</div><div className="mt-1 break-words">{result.error || result.reason}</div></>
                    )}
                </div>
            )}
        </div>
    );
}


/* ────────────────────────────── Top-level panel ───────────────────────── */

export default function LeadDeliveryPanel() {
    const [loading, setLoading] = useState(true);
    const [numbers, setNumbers] = useState([]);
    const [rules, setRules] = useState([]);
    const [wabaCfg, setWabaCfg] = useState({});
    const [tab, setTab] = useState("rules");

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const [n, r, c] = await Promise.all([
                api.get("/admin/lead-notifications/numbers"),
                api.get("/admin/lead-notifications/rules"),
                api.get("/admin/lead-notifications/waba-config"),
            ]);
            setNumbers(n.data); setRules(r.data); setWabaCfg(c.data);
        } catch { toast.error("Could not load Lead Notification settings."); }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { reload(); }, [reload]);

    if (loading && !rules.length) {
        return <div className="flex items-center justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;
    }

    const tabs = [
        { id: "rules",   label: "Form Rules",       icon: PhoneCall, count: rules.length },
        { id: "numbers", label: "WhatsApp Numbers", icon: MessageCircle, count: numbers.length },
        { id: "waba",    label: "WhatsApp API",     icon: Zap, dot: wabaCfg.live_mode, warn: !wabaCfg.configured },
        { id: "test",    label: "Test Notification", icon: Send },
        { id: "log",     label: "Delivery Log",     icon: Monitor },
    ];

    return (
        <div className="space-y-4" data-testid="lead-delivery-panel">
            <div>
                <h2 className="font-heading text-xl sm:text-2xl font-bold text-[#0F172A]">Lead Notifications &amp; Delivery</h2>
                <p className="text-xs text-slate-500 mt-1">
                    Decide where every new lead is delivered — Dashboard, Email, WhatsApp, or any combination. Each form has its own rule and WhatsApp recipient list.
                </p>
            </div>

            {/* Scrollable tab row for mobile */}
            <div className="flex gap-2 border-b border-slate-200 pb-2 overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]" data-testid="lead-delivery-tabs">
                {tabs.map((t) => {
                    const Icon = t.icon;
                    const active = tab === t.id;
                    return (
                        <button key={t.id} onClick={() => setTab(t.id)}
                            className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs sm:text-sm font-semibold shrink-0 ${
                                active ? "bg-[#1E3A8A] text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                            }`}
                            data-testid={`lead-delivery-tab-${t.id}`}>
                            <Icon className="w-4 h-4" />
                            <span className="whitespace-nowrap">{t.label}</span>
                            {typeof t.count === "number" && (
                                <span className={`inline-block min-w-[18px] text-center px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                                    active ? "bg-white/20 text-white" : "bg-slate-200 text-slate-600"
                                }`}>{t.count}</span>
                            )}
                            {t.dot && <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 inline-block" />}
                            {t.warn && <span className="w-1.5 h-1.5 rounded-full bg-amber-400 inline-block" />}
                        </button>
                    );
                })}
            </div>

            {tab === "rules"   && <RulesMatrix rules={rules} numbers={numbers} reload={reload} />}
            {tab === "numbers" && <NumbersManager numbers={numbers} reload={reload} />}
            {tab === "waba"    && <WabaConfig cfg={wabaCfg} reload={reload} />}
            {tab === "test"    && <TestNotification rules={rules} numbers={numbers} />}
            {tab === "log"     && <DeliveryLog numbers={numbers} />}
        </div>
    );
}
